import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { listConversations, setConversationArchived } from "../../lib/api/conversation";
import { useAuth } from "../../lib/auth";
import { initials, isRealInstant, relative } from "../../lib/format";
import { parseOperatingMode } from "../../lib/types/enums";
import type { Conversation } from "../../lib/types/models";
import { ChatIcon, InboxIcon } from "@ui/react/icons";
import { Avatar, Button, ChipToggle, EmptyState, ErrorState, SkeletonList } from "@ui/react";
import { errorMessage } from "../../lib/errorMessage";
import { routerLink } from "../../lib/routerLink";
import { lastOpened } from "./threadRead";

interface ListState {
  loading: boolean;
  error: unknown;
  conversations: Conversation[] | null;
}

type Tab = "current" | "archive";

const TABS: { id: Tab; label: string }[] = [
  { id: "current", label: "Current" },
  { id: "archive", label: "Archive" },
];

/**
 * The inbox, over REST rather than a Firestore listener — deliberately.
 *
 * `jobTitle` is enriched server-side and does not exist on the conversation
 * document, so a snapshot would render a list of untitled rows. The query a
 * listener would need, `contractorId == uid` ordered by `lastMessageAt`, also
 * has no composite index in firestore.indexes.json and would fail outright.
 *
 * The cost of REST is staleness, paid off by refetching on window focus and
 * whenever the thread reports activity.
 */
export function ConversationList({
  selectedId,
  refreshToken = 0,
}: {
  selectedId?: string;
  refreshToken?: number;
}) {
  const { user, contractor } = useAuth();
  const uid = user?.uid ?? "";
  const bookable = parseOperatingMode(contractor?.operatingMode) === "bookable";
  const [state, setState] = useState<ListState>({
    loading: true,
    error: null,
    conversations: null,
  });
  const [tab, setTab] = useState<Tab>("current");
  // Conversation ids with an archive toggle in flight, so a slow double-click
  // cannot fire the request twice.
  const [archiving, setArchiving] = useState<Set<string>>(new Set());

  // Guards a slow response from landing on top of a newer one.
  const sequence = useRef(0);

  const load = useCallback(async (silent: boolean) => {
    const seq = ++sequence.current;
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const conversations = await listConversations();
      if (seq === sequence.current) setState({ loading: false, error: null, conversations });
    } catch (error) {
      if (seq !== sequence.current) return;
      // A background refresh that fails keeps the list it already has; only a
      // foreground load is allowed to replace content with an error.
      setState((s) =>
        silent && s.conversations ? s : { loading: false, error, conversations: null },
      );
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load, refreshToken]);

  useEffect(() => {
    const onFocus = () => void load(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  async function toggleArchived(conversation: Conversation) {
    const id = conversation.conversationId;
    if (archiving.has(id)) return;
    setArchiving((prev) => new Set(prev).add(id));
    try {
      const updated = await setConversationArchived(id, !conversation.archived);
      setState((s) => ({
        ...s,
        conversations: (s.conversations ?? []).map((c) =>
          c.conversationId === id ? updated : c,
        ),
      }));
    } catch {
      // Non-fatal: the row's button simply stays as it was, and the next
      // background refresh (focus, or a thread activity bump) will resync.
    } finally {
      setArchiving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  const tabs = (
    <div className="chip-scroller" role="group" aria-label="Filter conversations">
      {TABS.map((candidate) => (
        <ChipToggle key={candidate.id} pressed={candidate.id === tab} onToggle={() => setTab(candidate.id)}>
          {candidate.label}
        </ChipToggle>
      ))}
    </div>
  );

  if (state.loading && !state.conversations) {
    return (
      <div className="stack">
        {tabs}
        <div className="chat__list" role="status" aria-label="Loading conversations">
          <SkeletonList rows={5} height="4.5rem" />
        </div>
      </div>
    );
  }

  if (state.error && !state.conversations) {
    return (
      <div className="stack">
        {tabs}
        <div className="chat__list">
          <ErrorState message={errorMessage(state.error)} onRetry={() => void load(false)} />
        </div>
      </div>
    );
  }

  const allConversations = state.conversations ?? [];
  const conversations = allConversations.filter((c) =>
    tab === "archive" ? c.archived : !c.archived,
  );

  if (conversations.length === 0) {
    return (
      <div className="stack">
        {tabs}
        <div className="chat__list">
          {/* The old line — "when a homeowner starts a chat about one of your
              jobs" — described something she cannot do: the only route that
              creates a thread is "Ask a question" on a job, and it is
              CONTRACTOR-authenticated, while her own inbox tells her the
              contractor starts it. Both sides sat waiting. A bookable
              contractor read the same sentence about "your jobs", which his
              account does not have. */}
          <EmptyState
            title={tab === "archive" ? "Nothing archived" : "No conversations yet"}
            icon={<ChatIcon />}
            action={
              tab === "archive"
                ? undefined
                : bookable
                  ? { label: "Go to bookings", href: "/bookings" }
                  : { label: "Find jobs", href: "/jobs" }
            }
            renderLink={routerLink}
          >
            {tab === "archive"
              ? "Conversations you archive, or that complete, land here."
              : bookable
                ? "Chats about your bookings land here — open one from any booking."
                : "Chats about your jobs land here — open one from any job with Ask a question."}
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      {tabs}
      <nav className="chat__list" aria-label="Conversations">
        {conversations.map((conversation) => {
          // The customer, not the job, is what tells two "Leaking tap" threads
          // apart — and it is what he needs at the gate. `userName` is enriched
          // server-side; the job title drops to the line below it.
          const jobTitle = conversation.jobTitle || "Untitled job";
          const title = conversation.userName?.trim() || jobTitle;
          const selected = conversation.conversationId === selectedId;
          const busy = archiving.has(conversation.conversationId);
          // Unread is per-device and local (threadRead.ts): newer than the last
          // time THIS browser opened the thread. No backend change, and it
          // degrades to "never unread" where storage is blocked.
          const opened = lastOpened(uid, conversation.conversationId);
          const lastMessageAt = isRealInstant(conversation.lastMessageAt)
            ? Date.parse(conversation.lastMessageAt ?? "") || 0
            : 0;
          const unread = lastMessageAt > 0 && (!opened || lastMessageAt > opened);
          return (
            <div key={conversation.conversationId} className="row" aria-current={selected ? "true" : undefined}>
              {/* A STACK, not a cluster: the Archive button carries `.btn`'s
                  white-space: nowrap and a ~202px min-content width, and
                  sharing a flex line with it left the text block 46px — a
                  `.row__title` that measured 0px in the 22rem split column,
                  and a row that told you nothing about which job it was. Its
                  own line, exactly as the homeowner inbox does it. */}
              <span className="stack stack--tight">
                <Link to={`/messages/${conversation.conversationId}`} className="cluster cluster--nowrap">
                  <Avatar name={title} initials={initials(title)} size="sm" />
                  <span className="stack stack--tight fill">
                    <span className="row__top">
                      <span className="row__title truncate">{title}</span>
                      {unread ? <span className="row__unread" aria-label="Unread" /> : null}
                      {isRealInstant(conversation.lastMessageAt) ? (
                        <span className="row__when">{relative(conversation.lastMessageAt)}</span>
                      ) : null}
                    </span>
                    {conversation.userName?.trim() ? (
                      <span className="row__sub truncate">{jobTitle}</span>
                    ) : null}
                    <span className="row__preview">
                      {conversation.lastMessageContent || "No messages yet"}
                    </span>
                    {conversation.status === "blocked" ? (
                      <span className="label-sm muted">This conversation is blocked.</span>
                    ) : null}
                  </span>
                </Link>
                <Button
                  variant="secondary"
                  className="row__action"
                  loading={busy}
                  // The name is on the button, so the label below it is free to
                  // go on a phone without the control losing what it is.
                  aria-label={conversation.archived ? "Move to current" : "Archive conversation"}
                  onClick={() => void toggleArchived(conversation)}
                >
                  <InboxIcon />
                  <span className="row__action-label">
                    {conversation.archived ? "Move to current" : "Archive conversation"}
                  </span>
                </Button>
              </span>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
